// First: a blocked localStorage throws on touch, and later imports read it.
import './lib/storageShim';
import './lib/canvasShim';
import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { LangProvider } from './lib/i18n';
import './styles.css';

// A lazy chunk that fails to load means this tab is running a bundle from
// before the last deploy. Vite raises 'vite:preloadError' for exactly that;
// the right response is a reload, which picks up the new index and its
// chunks. Rate-limited to one reload a minute per tab, so a genuinely broken
// deploy reloads once and then falls through to the error boundary instead
// of looping - and a later deploy, minutes or days on, still gets its reload.
window.addEventListener('vite:preloadError', (ev) => {
  try {
    const last = Number(sessionStorage.getItem('mm.chunkReload') || 0);
    if (Date.now() - last < 60_000) return;          // already tried this minute
    sessionStorage.setItem('mm.chunkReload', String(Date.now()));
  } catch { /* storage blocked: reload anyway, the boundary is still behind us */ }
  ev.preventDefault();
  window.location.reload();
});

// PageErrorBoundary in App.tsx catches a page that fails to render, but it only
// wraps the page outlet. Everything around it — sidebar, ticker tape, top bar,
// bottom nav, the auth modal, the landing page — sits outside, and a throw in
// any of those unmounts the whole tree: a blank white screen with no message
// and no way back, because the retry button lives inside the part that just
// disappeared. This is the floor under all of it.
//
// Outside LangProvider on purpose, so it also catches a failure in the language
// context itself. That is why the copy here is hard-coded in both languages
// rather than translated.
class AppErrorBoundary extends React.Component<{ children: React.ReactNode }, { err: Error | null }> {
  state: { err: Error | null } = { err: null };
  static getDerivedStateFromError(err: Error) { return { err }; }
  componentDidCatch(err: unknown, info: unknown) { console.error('[app] fatal render error:', err, info); }
  render() {
    if (!this.state.err) return this.props.children;
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 14, padding: 24,
        background: '#0b1220', color: '#e6edf7', textAlign: 'center',
        font: '15px/1.6 system-ui, -apple-system, "Segoe UI", sans-serif',
      }}>
        <div style={{ fontSize: 34 }}>⚠️</div>
        <div style={{ fontWeight: 600, fontSize: 18 }}>หน้าเว็บมีปัญหา · Something went wrong</div>
        <div style={{ opacity: 0.7, maxWidth: 460 }}>
          ลองโหลดหน้าใหม่อีกครั้ง หากยังไม่หายกรุณาแจ้งทีมงาน
          <br />
          Please reload. If it keeps happening, let us know.
        </div>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: 6, padding: '10px 22px', borderRadius: 10, cursor: 'pointer',
            border: '1px solid #00e0b0', background: 'transparent', color: '#00e0b0',
            font: 'inherit', fontWeight: 600,
          }}
        >
          โหลดใหม่ · Reload
        </button>
      </div>
    );
  }
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <AppErrorBoundary>
      <LangProvider>
        <App />
      </LangProvider>
    </AppErrorBoundary>
  </React.StrictMode>,
);
